import {ArrowRightIcon, ChevronDownIcon} from "lucide-react";
import {useId, useState} from "react";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {cn} from "@/lib/utils";
import {
  FORM_SHEET_ALERT_CLASS,
  FORM_SHEET_ALERT_DESCRIPTION_CLASS,
  FORM_SHEET_ALERT_TITLE_CLASS,
} from "@/src/modules/core/lib/form-sheet-alert";
import {formatDate} from "@/src/modules/core/lib/formatters";
import type {VehicleSafelistingsAccountability} from "@/src/modules/vehicles/types";

import {computeLimitBanner, type LimitBannerDescriptor} from "../lib/compute-limit-banner";

import {LimitUsagePanel} from "./limit-usage-panel";

type SafelistingLimitBannerProps = {
  accountability: VehicleSafelistingsAccountability | null;
  totalDays: number;
  /** Precomputed banner; when omitted, falls back to client limit math (edit flow). */
  descriptor?: LimitBannerDescriptor | null;
  /** Property coverage vocabulary — `night` for overnight ranges, `date` otherwise. */
  dateWord?: "night" | "date";
  /** Residents cannot use staff override path — copy omits override prompts. */
  showStaffOverrideFields?: boolean;
  isAvailabilityLoading?: boolean;
};

function rangeUnit(dateWord: "night" | "date", count: number): string {
  if (dateWord === "night") {
    return count === 1 ? "night" : "nights";
  }
  return count === 1 ? "date" : "dates";
}

export function SafelistingLimitBanner({
  accountability,
  totalDays,
  descriptor: descriptorProp,
  dateWord = "date",
  showStaffOverrideFields = true,
  isAvailabilityLoading = false,
}: SafelistingLimitBannerProps) {
  const usagePanelId = useId();
  const [usageOpen, setUsageOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isResidentView = !showStaffOverrideFields;
  const descriptor = descriptorProp ?? computeLimitBanner(accountability, totalDays);
  const usedSources = accountability?.used_sources ?? null;
  const resolvedUsedSources = usedSources ?? {
    safelisting_count: 0,
    truncated: false,
    items: [],
  };
  const usageCount = resolvedUsedSources.safelisting_count;
  const canOpenUsage = showStaffOverrideFields && (Boolean(usedSources) || descriptor?.used != null);

  if (!descriptor) return null;

  if (isAvailabilityLoading) {
    return (
      <div className="border-border/60 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2.5 text-sm">
        Checking permit availability…
      </div>
    );
  }

  const toggleUsage = () => setUsageOpen((open) => !open);

  const usagePanel =
    canOpenUsage && usageOpen ? (
      <LimitUsagePanel
        id={usagePanelId}
        usedSources={resolvedUsedSources}
        rollingDays={descriptor.rollingDays}
        nextReset={descriptor.nextReset}
      />
    ) : null;

  const usageToggleLabel = usageOpen
    ? "Hide usage"
    : `Review usage${usageCount > 0 ? ` (${usageCount})` : ""}`;

  const usageButton = canOpenUsage ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        "group h-8 w-fit shrink-0 px-3 text-xs font-semibold",
        getUsageButtonClass(descriptor.variant),
      )}
      aria-expanded={usageOpen}
      aria-controls={usagePanelId}
      onClick={toggleUsage}
    >
      {usageOpen
        ? "Hide usage"
        : usageToggleLabel}
      <ArrowRightIcon
        data-icon="inline-end"
        className={cn(
          "size-3.5 transition-transform group-hover:translate-x-0.5",
          usageOpen && "rotate-90",
        )}
      />
    </Button>
  ) : null;

  if (descriptor.variant === "info") {
    return (
      <div className="flex flex-col gap-3">
        <div className="border-border/60 bg-muted/40 flex flex-col gap-2.5 rounded-md border px-3 py-2.5">
          <p className="text-sm leading-snug">
            <span className="text-foreground font-medium tabular-nums">
              {descriptor.remaining} of {descriptor.totalAllowed} permits available
            </span>
            {descriptor.totalDays > 0 && descriptor.remainingAfterSubmit != null ? (
              <span className="text-muted-foreground">
                {" · "}
                Uses {descriptor.totalDays} {rangeUnit(dateWord, descriptor.totalDays)} ·{" "}
                {descriptor.remainingAfterSubmit} permit
                {descriptor.remainingAfterSubmit === 1 ? "" : "s"} left after submit
              </span>
            ) : null}
          </p>
          {usageButton}
        </div>
        {usagePanel}
      </div>
    );
  }

  const hasOptionalDetails = hasCollapsiblePermitDetails(descriptor);
  const permitDetails = hasOptionalDetails ? (
    <OptionalPermitDetails descriptor={descriptor} />
  ) : null;

  if (isResidentView) {
    return (
      <Alert variant={descriptor.variant} className={FORM_SHEET_ALERT_CLASS}>
        <AlertTitle className={cn(FORM_SHEET_ALERT_TITLE_CLASS, "font-normal")}>
          <PermitHeadlineText descriptor={descriptor} dateWord={dateWord} />
        </AlertTitle>
        <AlertDescription className={FORM_SHEET_ALERT_DESCRIPTION_CLASS}>
          <BlockingPermitCopy
            descriptor={descriptor}
            dateWord={dateWord}
            showStaffOverrideFields={showStaffOverrideFields}
          />
          {permitDetails ? (
            <div className="flex flex-col gap-1.5 pt-2">{permitDetails}</div>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
        <Alert variant={descriptor.variant} className={FORM_SHEET_ALERT_CLASS}>
          <AlertTitle
            className={cn(
              FORM_SHEET_ALERT_TITLE_CLASS,
              "line-clamp-none flex items-start justify-between gap-2 font-normal",
            )}
          >
            <span className="min-w-0 flex-1 text-pretty">
              <PermitHeadlineText descriptor={descriptor} dateWord={dateWord} />
            </span>
            {hasOptionalDetails ? (
              <CollapsibleTrigger
                type="button"
                className="text-current/70 hover:text-current inline-flex shrink-0 items-center gap-1 self-start text-xs font-medium whitespace-nowrap"
              >
                {detailsOpen ? "Hide details" : "Show details"}
                <ChevronDownIcon
                  data-icon="inline-end"
                  className={cn("size-3.5 transition-transform", detailsOpen && "rotate-180")}
                />
              </CollapsibleTrigger>
            ) : null}
          </AlertTitle>
          <AlertDescription className={FORM_SHEET_ALERT_DESCRIPTION_CLASS}>
            <BlockingPermitCopy
              descriptor={descriptor}
              dateWord={dateWord}
              showStaffOverrideFields={showStaffOverrideFields}
            />
            {hasOptionalDetails ? (
              <CollapsibleContent className="flex flex-col gap-1.5 pt-2">
                <OptionalPermitDetails descriptor={descriptor} />
              </CollapsibleContent>
            ) : null}
            {usageButton}
          </AlertDescription>
        </Alert>
      </Collapsible>
      {usagePanel}
    </div>
  );
}

function BlockingPermitCopy({
  descriptor,
  dateWord,
  showStaffOverrideFields = true,
}: {
  descriptor: NonNullable<ReturnType<typeof computeLimitBanner>>;
  dateWord: "night" | "date";
  showStaffOverrideFields?: boolean;
}) {
  const {variant, remaining, totalDays: descriptorTotalDays, shortfall, availabilityReason} =
    descriptor;
  const showOverLimitUsage = variant === "destructive" && descriptor.used > descriptor.totalAllowed;
  const excludeLabel = dateWord === "night" ? "nights" : "dates";
  const unit = rangeUnit(dateWord, descriptorTotalDays);
  const shortfallUnit = shortfall != null ? rangeUnit(dateWord, shortfall) : null;

  if (availabilityReason?.trim()) {
    const showReasonInBody = availabilityReason.trim() !== descriptor.title.trim();
    return (
      <div className="flex flex-col gap-1.5">
        {showReasonInBody ? <p>{availabilityReason}</p> : null}
        {showOverLimitUsage ? (
          <p>
            Rolling usage: {descriptor.used} of {descriptor.totalAllowed} permit
            {descriptor.totalAllowed === 1 ? "" : "s"} already in use.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {showOverLimitUsage ? (
        <p>
          Rolling usage: {descriptor.used} of {descriptor.totalAllowed} permit
          {descriptor.totalAllowed === 1 ? "" : "s"} already in use.
        </p>
      ) : null}
      {variant === "warning" && shortfall != null ? (
        <p>
          {showStaffOverrideFields
            ? `Remove ${shortfall} ${shortfallUnit} from the range, exclude ${excludeLabel}, or open Which situation? under Overrides.`
            : `Remove ${shortfall} ${shortfallUnit} from the range or exclude ${excludeLabel} to continue.`}
        </p>
      ) : null}
      {variant === "destructive" && remaining === 0 && descriptorTotalDays > 0 ? (
        <p>
          {showStaffOverrideFields
            ? `Shorten the range, exclude ${excludeLabel}, or open Which situation? under Overrides.`
            : `Shorten the range or exclude ${excludeLabel} to continue.`}
        </p>
      ) : null}
    </div>
  );
}

function OptionalPermitDetails({
  descriptor,
}: {
  descriptor: NonNullable<ReturnType<typeof computeLimitBanner>>;
}) {
  const {remaining, totalAllowed, nextReset, rollingDays} = descriptor;
  const fullyAvailable = remaining === totalAllowed;
  const showRollingCopy = rollingDays != null && !fullyAvailable;
  const showNextPermitCopy = Boolean(nextReset) && !fullyAvailable;

  return (
    <>
      {showRollingCopy ? (
        <p>
          Each permit day returns after {rollingDays} days, starting with the oldest use.
        </p>
      ) : null}
      {showNextPermitCopy && nextReset ? (
        <p>Next permit frees up on {formatDate(nextReset)}.</p>
      ) : null}
    </>
  );
}

function hasCollapsiblePermitDetails(
  descriptor: NonNullable<ReturnType<typeof computeLimitBanner>>,
) {
  const {remaining, totalAllowed, nextReset, rollingDays} = descriptor;
  const fullyAvailable = remaining === totalAllowed;
  return (
    (rollingDays != null && !fullyAvailable) || (Boolean(nextReset) && !fullyAvailable)
  );
}

function getUsageButtonClass(variant: "info" | "warning" | "destructive") {
  if (variant === "info") {
    return "border-border bg-background text-foreground hover:bg-muted/60";
  }
  if (variant === "warning") {
    return "mt-2 border-amber-400/80 bg-background text-amber-800 hover:bg-amber-50 hover:text-amber-900";
  }
  if (variant === "destructive") {
    return "mt-2 border-destructive/50 bg-background text-destructive hover:bg-destructive/5 hover:text-destructive";
  }
  return "";
}

function getPermitHeadline(
  descriptor: NonNullable<ReturnType<typeof computeLimitBanner>>,
  dateWord: "night" | "date",
) {
  if (descriptor.availabilityReason?.trim()) {
    return descriptor.title;
  }

  const {variant, remaining, totalAllowed, shortfall, totalDays: descriptorTotalDays} = descriptor;
  const unit = rangeUnit(dateWord, descriptorTotalDays);

  if (variant === "destructive") {
    if (remaining === 0 && descriptorTotalDays > 0) {
      return `No permits left for this ${descriptorTotalDays} ${unit} range`;
    }
    return `No permits available (${remaining} of ${totalAllowed} left)`;
  }

  if (variant === "warning") {
    if (shortfall != null && shortfall > 0 && descriptorTotalDays > 0) {
      return `This range uses ${descriptorTotalDays} ${unit}; only ${remaining} of ${totalAllowed} permits available`;
    }
    return `Only ${remaining} of ${totalAllowed} permits available`;
  }

  return descriptor.title;
}

function splitAfterFirstSentence(text: string): {lead: string; rest: string | null} {
  const trimmed = text.trim();
  const dotSpace = trimmed.indexOf(". ");
  if (dotSpace === -1) return {lead: trimmed, rest: null};
  return {
    lead: trimmed.slice(0, dotSpace + 1),
    rest: trimmed.slice(dotSpace + 2),
  };
}

function PermitHeadlineText({
  descriptor,
  dateWord,
}: {
  descriptor: NonNullable<ReturnType<typeof computeLimitBanner>>;
  dateWord: "night" | "date";
}) {
  const headline = getPermitHeadline(descriptor, dateWord);
  if (!descriptor.availabilityReason?.trim()) {
    return headline;
  }
  const {lead, rest} = splitAfterFirstSentence(headline);
  if (!rest) return headline;
  return (
    <>
      {lead}
      <br />
      {rest}
    </>
  );
}
